// Legacy function - kept for backward compatibility
function formatDate(originalDate?: string) {
  if (!originalDate) return '';

  const date = new Date(originalDate);
  // TODO: We may want a less locale specific or locale-aware variant of this
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true,
  });
}

/** Matches a date-only string as the backend sends it. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse a YYYY-MM-DD string at LOCAL midnight.
 *
 * `new Date('2026-07-08')` is UTC midnight, which reads back as the 7th anywhere west of
 * UTC — so date-only values must never go through the plain constructor (or through
 * `parseDate`, which uses it). Use this instead.
 *
 * Anything that is not a bare YYYY-MM-DD string is handed to `new Date` unchanged, since
 * a full timestamp already carries its own offset.
 */
export function parseDateOnly(dateValue: string | Date): Date {
  if (dateValue instanceof Date) return dateValue;

  if (DATE_ONLY.test(dateValue)) {
    const [year, month, day] = dateValue.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  return new Date(dateValue);
}

/**
 * Format date-only fields (Pattern B) from backend
 * Handles both string dates (YYYY-MM-DD) and Date objects
 * Returns formatted date without time information
 */
export function formatDateOnly(dateValue?: string | Date | null): string {
  if (!dateValue) return '';

  let date: Date;
  if (typeof dateValue === 'string') {
    // If already a full ISO timestamp, parse directly
    if (dateValue.includes('T')) {
      date = new Date(dateValue);
    } else {
      // Handle YYYY-MM-DD format from backend
      date = new Date(dateValue + 'T00:00:00');
    }
  } else {
    date = new Date(dateValue);
  }

  if (isNaN(date.getTime())) return '';

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * Format full timestamps (Pattern A & Pattern C) from backend
 * Handles both ISO strings and Date objects
 * Returns formatted date with time information
 */
export function formatDateTime(dateValue?: string | Date | null): string {
  if (!dateValue) return '';

  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return '';

  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
}

/**
 * Parse date for date-only submission (Pattern B)
 * Converts Date object to YYYY-MM-DD format for backend (timezone-agnostic)
 */
export function formatDateForSubmission(
  date: Date | string | null,
): string | null {
  if (!date) return null;

  let dateObj: Date;

  if (typeof date === 'string') {
    // Handle YYYY-MM-DD strings by parsing in local timezone to avoid UTC shift
    if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = date.split('-').map(Number);
      dateObj = new Date(year, month - 1, day); // Create in local timezone
    } else {
      dateObj = new Date(date);
    }
  } else {
    dateObj = date;
  }

  if (isNaN(dateObj.getTime())) return null;

  // Use local timezone formatting to avoid UTC conversion issues
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Format timestamp for submission (Pattern C)
 * Converts Date object to full ISO string for backend
 */
export function formatDateTimeForSubmission(
  date: Date | string | null,
): string | null {
  if (!date) return null;

  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return null;

  // Return full ISO string for timestamp fields
  return dateObj.toISOString();
}

/**
 * Safe date parsing that handles both string and Date inputs
 * Returns null for invalid dates
 */
export function parseDate(dateValue?: string | Date | null): Date | null {
  if (!dateValue) return null;

  // CLAUDE.md states the rule this function has to obey: a YYYY-MM-DD string must be parsed
  // as new Date(year, month-1, day), never through the plain constructor, which reads it as
  // UTC midnight and so reports the previous day anywhere west of UTC. Callers serialize
  // with local getters, so mixing the two silently shifts the date — and once the shifted
  // value is saved and read back, the loss compounds one day per save. That is exactly what
  // happened on mobile (U184); this keeps the same trap from being sprung here, since the
  // docs point developers at this function by name.
  if (typeof dateValue === 'string' && DATE_ONLY.test(dateValue)) {
    const [year, month, day] = dateValue.split('-').map(Number);
    const dateOnly = new Date(year, month - 1, day);

    // new Date(2026, 12, 45) rolls over into 2027 instead of failing, so an out-of-range
    // string would come back as a plausible wrong date. The plain constructor returned
    // Invalid Date for those and callers rely on the null.
    const isRealCalendarDay =
      dateOnly.getFullYear() === year &&
      dateOnly.getMonth() === month - 1 &&
      dateOnly.getDate() === day;

    return isRealCalendarDay ? dateOnly : null;
  }

  const date = new Date(dateValue);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Parse date from MM/DD/YYYY display format
 * Returns Date object or null if invalid
 */
export function parseDateFromDisplay(displayDate: string): Date | null {
  if (!displayDate) return null;

  // Parse MM/DD/YYYY format
  const parts = displayDate.split('/');
  if (parts.length !== 3) return null;

  const month = parseInt(parts[0], 10);
  const day = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);

  if (isNaN(month) || isNaN(day) || isNaN(year)) return null;

  // Create date with local timezone (month is 0-indexed in JS)
  const date = new Date(year, month - 1, day);

  // Validate the date is valid
  if (isNaN(date.getTime())) return null;

  // Check if the date components match what we put in (handles invalid dates like 02/31)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

/**
 * Format any date value for display (always MM/DD/YYYY)
 * Handles Date objects, YYYY-MM-DD strings, and MM/DD/YYYY strings
 */
export function formatForDisplay(
  dateValue: string | Date | null | undefined,
): string {
  if (!dateValue) return '';

  let date: Date;

  if (typeof dateValue === 'string') {
    // Check if it's already in MM/DD/YYYY format
    if (dateValue.match(/^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/)) {
      return dateValue;
    }

    // Check if it's YYYY-MM-DD format
    if (dateValue.match(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/)) {
      // Parse YYYY-MM-DD and convert to MM/DD/YYYY (timezone-agnostic)
      const [year, month, day] = dateValue.split('-');
      return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
    }

    // Try to parse as date (timezone-agnostic for YYYY-MM-DD)
    if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Parse YYYY-MM-DD in local timezone to avoid UTC shift
      const [year, month, day] = dateValue.split('-').map(Number);
      date = new Date(year, month - 1, day);
    } else {
      date = new Date(dateValue);
    }
  } else {
    date = new Date(dateValue);
  }

  if (isNaN(date.getTime())) return '';

  // Format as MM/DD/YYYY
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
}

/**
 * Check if a string is a valid date format
 * Accepts both MM/DD/YYYY and YYYY-MM-DD formats
 */
export function isValidDateFormat(dateString: string): boolean {
  if (!dateString) return false;

  // Check MM/DD/YYYY format
  const displayFormat = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
  if (displayFormat.test(dateString)) {
    const date = parseDateFromDisplay(dateString);
    return date !== null;
  }

  // Check YYYY-MM-DD format
  const backendFormat = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
  if (backendFormat.test(dateString)) {
    // Parse in local timezone to avoid UTC shift
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return !isNaN(date.getTime());
  }

  return false;
}

/**
 * Format dates for GraphQL requests based on field pattern
 * Pattern A: Auto-generated timestamps - skip, server handles
 * Pattern B: Date-only fields - format as YYYY-MM-DD strings
 * Pattern C: User timestamps - format as ISO strings
 */
export function formatDateForRequest(
  dateValue: string | Date | null | undefined,
  isDateOnly: boolean = false,
): string | Date | null {
  if (!dateValue) return null;

  if (isDateOnly) {
    // Pattern B: Date-only fields should be YYYY-MM-DD strings
    return formatDateForSubmission(dateValue);
  } else {
    // Pattern C: DateTime fields - keep as Date objects for ISO serialization
    // Return Date object so GraphQL can serialize it properly
    const date = parseDate(dateValue);
    return date;
  }
}

/**
 * Get date section header for activity grouping
 * Returns "Today", "Yesterday", or formatted date string
 */
export function getDateSectionHeader(date: Date): string {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  
  // Reset time to compare dates only
  const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const compareToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const compareYesterday = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
  
  if (compareDate.getTime() === compareToday.getTime()) {
    return 'Today';
  } else if (compareDate.getTime() === compareYesterday.getTime()) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  }
}

/**
 * Get activity date for grouping purposes
 * Uses createdAt if available, falls back to time
 */
export function getActivityDate(activity: { createdAt?: string | Date | null; time?: string | Date | null }): Date | null {
  const dateValue = activity.createdAt || activity.time;
  return parseDate(dateValue);
}

/**
 * Format time for activities with timezone-independent date-time display
 * Shows consistent MM-DD-YYYY - HH:MM pm format for all activities
 */
export function formatActivityTime(dateValue?: string | Date | null): string {
  if (!dateValue) return '';

  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return '';

  // Format all activities with MM-DD-YYYY - HH:MM pm format
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  const timeStr = date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).toLowerCase(); // Convert to lowercase (am/pm)

  return `${month}-${day}-${year} - ${timeStr}`;
}

/**
 * Group activities by date sections
 * Returns activities grouped by "Today", "Yesterday", or specific dates
 */
export function groupActivitiesByDate<T extends { createdAt?: string | Date | null; time?: string | Date | null }>(
  activities: T[]
): Array<{ dateHeader: string; activities: T[] }> {
  // Group activities by date string
  const dateGroups = new Map<string, T[]>();
  
  activities.forEach(activity => {
    const activityDate = getActivityDate(activity);
    if (!activityDate) return;
    
    const dateHeader = getDateSectionHeader(activityDate);
    
    if (!dateGroups.has(dateHeader)) {
      dateGroups.set(dateHeader, []);
    }
    dateGroups.get(dateHeader)!.push(activity);
  });
  
  // Convert to sorted array (Today first, Yesterday second, then chronologically)
  const sortedGroups = Array.from(dateGroups.entries()).map(([dateHeader, activities]) => ({
    dateHeader,
    activities
  }));
  
  // Custom sort: Today, Yesterday, then by date (newest first)
  sortedGroups.sort((a, b) => {
    if (a.dateHeader === 'Today') return -1;
    if (b.dateHeader === 'Today') return 1;
    if (a.dateHeader === 'Yesterday') return -1;
    if (b.dateHeader === 'Yesterday') return 1;
    
    // For specific dates, sort by date (newest first)
    const dateA = new Date(a.dateHeader);
    const dateB = new Date(b.dateHeader);
    return dateB.getTime() - dateA.getTime();
  });
  
  return sortedGroups;
}

export default formatDate;
